import {
  AdminSpinner,
  AIStarsIcon,
  Button,
  Div,
  H3,
  Icon,
  Tab,
} from '@vgrubs/components';
import {
  MenuItemV2Response,
  MenuV2Response,
} from 'components/src/types/menu.types';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useParams } from 'react-router';
import { MenuAISplitModal } from './MenuAISplitModal';
import { aiChangeDomId, MenuAIChangesPanel } from './MenuAIChangesPanel';
import {
  MenuAIControlsHandle,
  MenuAIControlsPanel,
} from './MenuAIControlsPanel';
import { hasItemChange } from './menuAiChanges.helpers';
import { aiItemDomId, MenuAIPreviewSections } from './MenuAIPreviewSections';
import { RefineItemModal } from './RefineItemModal';
import { RevertItemModal } from './RevertItemModal';
import { useMenuAiImprove } from './useMenuAiImprove';
import { useSelectedMenu } from '../menuSelection';

/**
 * Two side-by-side cards for the "Improve Menu with AI" flow.
 * Left  (~70%) = read-only preview of the current/draft menu (+ price-change
 *                summary); glows with a flowing gradient border + shimmer while
 *                the AI is running.
 * Right (~30%) = AI controls (improvement toggles/prompts → progress → results)
 *                + the Cancel/Save footer.
 *
 * Kept always-mounted in Menu.tsx so the socket in `useMenuAiImprove` survives
 * the modal being closed and a late `menu_ai_finished:saved` still refreshes
 * the live menu.
 *
 * Exposes `discardDraft` via ref so the "Improve Menu with AI" button can wipe
 * any lingering draft (with a full-screen loader) BEFORE opening — see Menu.tsx.
 */
export type MenuAIImproveHandle = {
  // Delete any lingering draft; resolves true when it's safe to open the modal.
  discardDraft: () => Promise<boolean>;
};

export const MenuAIImprove = forwardRef<
  MenuAIImproveHandle,
  {
    open: boolean;
    currentMenu: MenuV2Response | null;
    onClose: () => void;
    onSaved: () => void;
  }
>(({ open, currentMenu, onClose, onSaved }, ref) => {
  const { locationId, brandId } = useParams() as {
    locationId: string;
    brandId: string;
  };
  const { menuId } = useSelectedMenu();
  const [refineTarget, setRefineTarget] = useState<MenuItemV2Response | null>(
    null
  );
  // Item pending a revert-confirmation (null = modal closed). Revert wipes all
  // of an item's AI changes, so it's gated behind RevertItemModal.
  const [revertTargetId, setRevertTargetId] = useState<number | null>(null);
  // Set of collapsed category ids. Empty = all expanded (the default). Lifted
  // here so the "Collapse all categories" button can drive every category.
  const [collapsedCategoryIds, setCollapsedCategoryIds] = useState<Set<number>>(
    new Set()
  );
  // Right-card tab. Lives here (not in the card) because the card's children
  // unmount when the modal closes; surviving close/reopen means a reopened
  // ready draft lands back on "Changes".
  const [aiTab, setAiTab] = useState<'improve' | 'changes'>('improve');
  // Item currently playing the focus glare (set by clicking a name in Changes).
  const [highlightItemId, setHighlightItemId] = useState<number | null>(null);
  // Reverse direction: change box in the right panel playing the glare (set by
  // clicking an item in the left preview).
  const [highlightChangeId, setHighlightChangeId] = useState<number | null>(
    null
  );
  const controlsRef = useRef<MenuAIControlsHandle>(null);

  const {
    status,
    progress,
    draftData,
    loading,
    discarding,
    startImprove,
    refineItem,
    undoItem,
    saveDraft,
    reset,
    discardDraft,
  } = useMenuAiImprove({ locationId, brandId, menuId, onSaved });

  // The fresh-start discard runs on the button click (before open), not here —
  // exposed via ref so Menu.tsx can gate opening on its success.
  useImperativeHandle(ref, () => ({ discardDraft }));

  // On close: dismiss sub-modals and wipe a FAILED run so it doesn't linger.
  useEffect(() => {
    if (!open) {
      setRefineTarget(null);
      if (status === 'failed') reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const leftMenu = draftData?.menu ?? currentMenu;
  const busy = status === 'pending' || status === 'processing';

  const changes = draftData?.changes ?? [];
  // Modifier changes are now nested per item, so the count is the number of
  // items the AI actually touched (matches what the Changes panel lists).
  const changedCount = changes.filter(hasItemChange).length;
  const hasChanges = changedCount > 0;
  // Never trust raw tab state: after a reset clears draftData, fall back to
  // the controls panel.
  const effectiveTab = hasChanges ? aiTab : 'improve';

  // Auto-switch: a fresh draft opens on "Changes"; starting a (re-)run jumps
  // back to "Improve" so the progress view is visible. Keyed on status only —
  // a refine that alters `changes` while 'ready' must not yank the tab.
  useEffect(() => {
    if (status === 'ready' && hasChanges) setAiTab('changes');
    if (status === 'pending') setAiTab('improve');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const categoryIds = leftMenu?.categories.map((c) => c.id) ?? [];
  const allCollapsed =
    categoryIds.length > 0 &&
    categoryIds.every((id) => collapsedCategoryIds.has(id));

  const toggleCategory = (id: number) =>
    setCollapsedCategoryIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAllCategories = () =>
    setCollapsedCategoryIds(allCollapsed ? new Set() : new Set(categoryIds));

  // Cancels the in-flight focus (pending timers + IntersectionObserver) so a
  // second click doesn't glare the previous item mid-scroll.
  const focusCancelRef = useRef<(() => void) | null>(null);

  // Focus an item in the left preview from the Changes list: expand its category
  // if collapsed, scroll it into view, then play a one-shot glare — but only once
  // the item is actually IN VIEW (the smooth scroll is async, so glaring right
  // after scrollIntoView would play it off-screen). When the category has to
  // expand we first wait out the 300ms Collapsible transition.
  const focusItem = (itemId: number) => {
    focusCancelRef.current?.();

    const category = leftMenu?.categories.find((c) =>
      c.items.some((it) => it.id === itemId)
    );
    if (!category) return;

    const wasCollapsed = collapsedCategoryIds.has(category.id);
    if (wasCollapsed) {
      setCollapsedCategoryIds((prev) => {
        const next = new Set(prev);
        next.delete(category.id);
        return next;
      });
    }
    // Clear first so re-clicking the same item restarts the (mount-once) glare.
    setHighlightItemId(null);

    let cancelled = false;
    let observer: IntersectionObserver | null = null;
    const timers: number[] = [];
    const cleanup = () => {
      cancelled = true;
      observer?.disconnect();
      timers.forEach((t) => window.clearTimeout(t));
    };
    focusCancelRef.current = cleanup;

    const glare = () => {
      if (cancelled) return;
      cleanup();
      setHighlightItemId(itemId);
    };

    timers.push(
      window.setTimeout(
        () => {
          if (cancelled) return;
          const el = document.getElementById(aiItemDomId(itemId));
          if (!el) return glare();
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          if (!('IntersectionObserver' in window)) return glare();
          // Glare when the item is mostly visible…
          observer = new IntersectionObserver(
            (entries) => {
              if (
                entries.some(
                  (e) => e.isIntersecting && e.intersectionRatio >= 0.6
                )
              )
                glare();
            },
            { threshold: [0, 0.6, 1] }
          );
          observer.observe(el);
          // …with a fallback in case that threshold is never reached.
          timers.push(window.setTimeout(glare, 900));
        },
        wasCollapsed ? 320 : 0
      )
    );
  };

  // Auto-clear the highlight after the glare (~0.9s) finishes; cancel any
  // in-flight focus on unmount.
  useEffect(() => {
    if (highlightItemId == null) return;
    const t = window.setTimeout(() => setHighlightItemId(null), 1000);
    return () => window.clearTimeout(t);
  }, [highlightItemId]);

  useEffect(() => () => focusCancelRef.current?.(), []);

  // Reverse of focusItem: from a preview-item click, switch to the Changes tab
  // and scroll+glare that item's change box. When the tab has to switch we wait
  // a frame for the panel to mount before querying its DOM node.
  const focusChangeCancelRef = useRef<(() => void) | null>(null);

  const focusChange = (itemId: number) => {
    if (!hasChanges) return;
    focusChangeCancelRef.current?.();

    const needsTabSwitch = effectiveTab !== 'changes';
    setAiTab('changes');
    // Clear first so re-clicking the same item restarts the (mount-once) glare.
    setHighlightChangeId(null);

    let cancelled = false;
    let observer: IntersectionObserver | null = null;
    const timers: number[] = [];
    const cleanup = () => {
      cancelled = true;
      observer?.disconnect();
      timers.forEach((t) => window.clearTimeout(t));
    };
    focusChangeCancelRef.current = cleanup;

    const glare = () => {
      if (cancelled) return;
      cleanup();
      setHighlightChangeId(itemId);
    };

    timers.push(
      window.setTimeout(
        () => {
          if (cancelled) return;
          const el = document.getElementById(aiChangeDomId(itemId));
          if (!el) return glare();
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          if (!('IntersectionObserver' in window)) return glare();
          observer = new IntersectionObserver(
            (entries) => {
              if (
                entries.some(
                  (e) => e.isIntersecting && e.intersectionRatio >= 0.6
                )
              )
                glare();
            },
            { threshold: [0, 0.6, 1] }
          );
          observer.observe(el);
          timers.push(window.setTimeout(glare, 900));
        },
        needsTabSwitch ? 60 : 0
      )
    );
  };

  useEffect(() => {
    if (highlightChangeId == null) return;
    const t = window.setTimeout(() => setHighlightChangeId(null), 1000);
    return () => window.clearTimeout(t);
  }, [highlightChangeId]);

  useEffect(() => () => focusChangeCancelRef.current?.(), []);

  const confirmRevert = async () => {
    if (revertTargetId == null) return;
    await undoItem(revertTargetId);
    setRevertTargetId(null);
  };

  return (
    <>
      {discarding && <AdminSpinner />}
      <MenuAISplitModal
        open={open}
        animateLeft={busy}
        onClose={onClose}
        left={
          <>
            {leftMenu && leftMenu.categories.length > 0 && (
              <Div
                $flex
                $between
                $alignCenter
                $mb="16"
                style={{ flexWrap: 'wrap', gap: '12px' }}
              >
                <H3 $m="0" $left>
                  {/* draftData (not isReady): a re-run keeps the draft shown */}
                  {draftData ? 'Improved menu' : 'Current menu'}
                </H3>
                <Button
                  $small
                  $selected
                  $gray
                  $w="auto"
                  onClick={toggleAllCategories}
                >
                  {allCollapsed
                    ? 'Expand all categories'
                    : 'Collapse all categories'}
                </Button>
              </Div>
            )}
            <MenuAIPreviewSections
              menu={leftMenu}
              // Refine is available whenever a draft is shown — including a busy
              // re-run on top of an existing draft, where it stays visible but
              // disabled rather than disappearing.
              onRefine={draftData ? (item) => setRefineTarget(item) : undefined}
              onRevert={draftData ? setRevertTargetId : undefined}
              onItemClick={draftData ? focusChange : undefined}
              refineDisabled={busy}
              priceChanges={draftData?.priceChangeSummary?.rows}
              changes={changes}
              collapsedIds={collapsedCategoryIds}
              onToggleCategory={toggleCategory}
              highlightItemId={highlightItemId}
            />
          </>
        }
        right={
          <>
            {hasChanges && (
              <Div $flex $gap="12px" $mb="20" $width="100%">
                {/* flex:1 (overrides Tab's flex:none) so the two tabs split the
                    row evenly; $width="100%" would render each at full width and
                    overflow the card. minWidth:0 lets them shrink below content. */}
                <Tab
                  $minWidthAuto
                  style={{ flex: 1, minWidth: 0 }}
                  selected={effectiveTab === 'improve'}
                  onClick={() => setAiTab('improve')}
                >
                  Improve
                </Tab>
                <Tab
                  $minWidthAuto
                  style={{ flex: 1, minWidth: 0 }}
                  selected={effectiveTab === 'changes'}
                  onClick={() => setAiTab('changes')}
                >
                  Changes ({changedCount})
                </Tab>
              </Div>
            )}
            {/* Kept mounted (display:none) so the panel's form state and the
                footer's controlsRef submit survive tab switches. While busy the
                wrapper fills the space under the tabs and centers the progress
                view; otherwise it's a natural-height block so the (tall) form
                scrolls normally within AiScroll instead of being clipped. */}
            <div
              style={
                effectiveTab !== 'improve'
                  ? { display: 'none' }
                  : busy
                  ? {
                      display: 'flex',
                      flexDirection: 'column',
                      flex: 1,
                      minHeight: 0,
                      justifyContent: 'center',
                    }
                  : { display: 'block' }
              }
            >
              <MenuAIControlsPanel
                ref={controlsRef}
                status={status}
                progress={progress}
                onImprove={startImprove}
              />
            </div>
            {effectiveTab === 'changes' && (
              <MenuAIChangesPanel
                changes={changes}
                onItemClick={focusItem}
                onRevert={setRevertTargetId}
                highlightItemId={highlightChangeId}
              />
            )}
          </>
        }
        footer={
          <Div $flex $column $gap="12px" $width="100%">
            <Button
              $blueGradient
              $selected
              $w="100%"
              $loading={busy}
              disabled={busy}
              onClick={() => controlsRef.current?.submit()}
            >
              <Div $flex $center $gap="8px">
                <Icon>
                  <AIStarsIcon />
                </Icon>
                {draftData ? 'Re-run AI' : 'Improve with AI'}
              </Div>
            </Button>
            <Div $flex $gap="12px" $width="100%">
              <Button $selected $gray $w="100%" onClick={onClose}>
                Cancel
              </Button>
              <Button
                $selected
                $w="100%"
                disabled={status !== 'ready'}
                $loading={status === 'processing'}
                onClick={saveDraft}
              >
                Save
              </Button>
            </Div>
          </Div>
        }
      />

      <RefineItemModal
        item={refineTarget}
        locationId={locationId}
        brandId={brandId}
        loading={loading}
        closeModal={() => setRefineTarget(null)}
        onRefine={refineItem}
      />

      <RevertItemModal
        isOpen={revertTargetId != null}
        loading={loading}
        closeModal={() => setRevertTargetId(null)}
        onConfirm={confirmRevert}
      />
    </>
  );
});

MenuAIImprove.displayName = 'MenuAIImprove';
